// MyInvites — post-login invitations step. Fetches the user's pending company
// invites (users/company-invites, loginToken) before company selection. When
// there are none (or the fetch fails), it falls through to /select-company.
// Shows the first invite as a detail card (per design); Accept (users/accept-invite)
// makes that company selectable, so we forward to /select-company to pick it.
// Ignore is a pure skip — it leaves the invite pending and moves on to company
// selection (there is no invitee-side reject route; the only update-invite-status
// call is the admin-side withdraw). The inviter, structure placement, roles and
// sent date all come off the invite row itself.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '@/components/common';
import { useGetCompanyInvitesQuery, useAcceptInviteMutation } from '@/api/endpoints/companyApi';
import type { ApiErrorShape } from '@/api/types';
import AuthLayout from '@/layout/AuthLayout';
import initialsOf from '@/utils/initials';
import { fmtDatePadded } from '@/utils/formatDate';

/** Role / node name lists render joined; an empty list shows an em dash. */
const listOf = (names: string[]) => (names.length ? names.join(', ') : '\u2014');

const MyInvites = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: invites = [], isLoading, isError, isSuccess } = useGetCompanyInvitesQuery();
  const [acceptInvite, { isLoading: accepting }] = useAcceptInviteMutation();

  // Nothing to act on (or the fetch failed) — go straight to company selection.
  useEffect(() => {
    if (isError || (isSuccess && invites.length === 0)) {
      navigate('/select-company', { replace: true });
    }
  }, [isError, isSuccess, invites.length, navigate]);

  const handleAccept = async (companyId: string) => {
    if (accepting) return;
    try {
      await acceptInvite({ companies: [companyId] }).unwrap();
      toast.success(t('toast_invite_accepted'));
      navigate('/select-company');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  // Nothing to send — skipping just defers the invite to the next login.
  const handleIgnore = () => navigate('/select-company', { replace: true });

  // Per design: show the first pending invite. Render nothing until it resolves
  // (the effect redirects when there are none).
  const invite = invites[0];

  return (
    <AuthLayout variant='login'>
      <div className='auth-page my-invites'>
        {isLoading || !invite ? (
          <p className='my-invites__status'>{t('text_loading')}</p>
        ) : (
          <>
            <span className='my-invites__avatar'>{initialsOf(invite.name)}</span>
            <h1 className='my-invites__name'>{invite.name}</h1>
            <p className='my-invites__invited-by'>
              {t('invites_invited_by', { inviter: invite.invitedBy })}
            </p>

            <div className='my-invites__detail'>
              <div className='my-invites__detail-row'>
                <span className='my-invites__detail-label'>{t('invites_company_structure')}</span>
                <span className='my-invites__detail-value'>{listOf(invite.nodes)}</span>
              </div>
              <div className='my-invites__detail-row'>
                <span className='my-invites__detail-label'>{t('invites_roles')}</span>
                <span className='my-invites__detail-value'>{listOf(invite.roles)}</span>
              </div>
            </div>

            <div className='my-invites__actions'>
              <Button size='lg' onClick={() => handleAccept(invite.id)} loading={accepting}>
                {t('action_accept')}
              </Button>
              <Button size='lg' variant='secondary' onClick={handleIgnore} disabled={accepting}>
                {t('action_ignore')}
              </Button>
            </div>

            <p className='my-invites__sent'>
              {t('invites_sent', { date: fmtDatePadded(invite.sentAt) })}
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default MyInvites;
